import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import NotificationBell from './NotificationBell';
import clsx from 'clsx';
import {
  ChartBarIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  ChartPieIcon,
  BuildingStorefrontIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import {
  ChartBarIcon as ChartBarIconSolid,
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
  ChartPieIcon as ChartPieIconSolid,
  BuildingStorefrontIcon as BuildingStorefrontIconSolid,
} from '@heroicons/react/24/solid';

interface LayoutProps {
  children: React.ReactNode;
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: ChartBarIcon, iconActive: ChartBarIconSolid },
  { name: 'Disputes', href: '/disputes', icon: ExclamationTriangleIcon, iconActive: ExclamationTriangleIconSolid, badge: true },
  { name: 'Orders', href: '/orders', icon: ClipboardDocumentListIcon, iconActive: ClipboardDocumentListIconSolid },
  { name: 'Analytics', href: '/analytics', icon: ChartPieIcon, iconActive: ChartPieIconSolid },
  { name: 'Stores', href: '/stores', icon: BuildingStorefrontIcon, iconActive: BuildingStorefrontIconSolid },
];

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Mock pending disputes count for badge
  const pendingDisputes = 23;

  // Check if using demo account
  const isDemoAccount = user?.email === 'demo@recoverrefunds.com';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Demo Account Banner */}
      {isDemoAccount && (
        <div className="bg-amber-500 text-amber-50 text-center py-1.5 px-4 text-sm font-medium">
          <span className="inline-flex items-center gap-2">
            <span className="animate-pulse">●</span>
            Demo Mode - This is sample data for evaluation. Data resets periodically.
            <Link to="/login" className="underline hover:no-underline ml-2">Exit Demo</Link>
          </span>
        </div>
      )}
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        'fixed inset-y-0 left-0 z-50 w-72 bg-slate-900 transform transition-transform duration-300 ease-in-out lg:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/30">
                <span className="text-lg font-bold text-white">R</span>
              </div>
              <span className="text-xl font-bold text-white tracking-tight">RecoverRefunds</span>
            </div>
            <button
              className="lg:hidden p-2 -mr-2 text-slate-400 hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            <p className="px-3 mb-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Main Menu
            </p>
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = isActive ? item.iconActive : item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    'group flex items-center gap-3 px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 min-h-[44px]',
                    isActive
                      ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg shadow-amber-500/30'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  )}
                >
                  <Icon className={clsx(
                    'w-5 h-5 flex-shrink-0 transition-transform duration-200',
                    !isActive && 'group-hover:scale-110'
                  )} />
                  <span className="flex-1">{item.name}</span>
                  {item.badge && pendingDisputes > 0 && (
                    <span className={clsx(
                      'flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-xs font-semibold rounded-full',
                      isActive
                        ? 'bg-white/20 text-white'
                        : 'bg-amber-500/20 text-amber-400'
                    )}>
                      {pendingDisputes}
                    </span>
                  )}
                </Link>
              );
            })}

            <div className="pt-6">
              <p className="px-3 mb-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Settings
              </p>
              <Link
                to="/settings"
                className={clsx(
                  'group flex items-center gap-3 px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 min-h-[44px]',
                  location.pathname === '/settings'
                    ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg shadow-amber-500/30'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                )}
              >
                <Cog6ToothIcon className="w-5 h-5 flex-shrink-0 group-hover:rotate-90 transition-transform duration-300" />
                <span>Settings</span>
              </Link>
            </div>
          </nav>

          {/* User menu at bottom */}
          <div className="p-4 border-t border-slate-700/50">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500">
                <span className="text-sm font-semibold text-white">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
              </div>
              <button
                onClick={logout}
                className="p-2.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:pl-72">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200/80">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Left side - mobile menu + search */}
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
                onClick={() => setSidebarOpen(true)}
              >
                <Bars3Icon className="w-6 h-6" />
              </button>

              <div className="hidden sm:flex items-center">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search orders, disputes..."
                    className="w-64 lg:w-80 pl-10 pr-4 py-2 text-sm bg-slate-100 border-0 rounded-xl focus:ring-2 focus:ring-amber-500 focus:bg-white transition-all"
                  />
                  <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden lg:inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-slate-400 bg-slate-200 rounded">
                    <span className="text-xs">&#8984;</span>K
                  </kbd>
                </div>
              </div>
            </div>

            {/* Right side - notifications + user */}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <NotificationBell />

              {/* Divider */}
              <div className="hidden sm:block w-px h-6 bg-slate-200 mx-2" />

              {/* User dropdown */}
              <div className="relative">
                <button
                  className="flex items-center gap-3 p-1.5 pr-3 hover:bg-slate-100 rounded-xl transition-colors"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 text-white text-sm font-semibold">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-slate-900">
                      {user?.first_name}
                    </p>
                  </div>
                </button>

                {/* Dropdown menu */}
                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <p className="text-sm font-medium text-slate-900">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-xs text-slate-500">{user?.email}</p>
                      </div>
                      <Link
                        to="/settings"
                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <UserCircleIcon className="w-5 h-5 text-slate-400" />
                        Your Profile
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Cog6ToothIcon className="w-5 h-5 text-slate-400" />
                        Settings
                      </Link>
                      <div className="border-t border-slate-100 mt-1 pt-1">
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            logout();
                          }}
                          className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <ArrowRightOnRectangleIcon className="w-5 h-5" />
                          Sign out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
